import React from 'react';
import { Link } from 'react-router-dom';
import Sparkles from '../components/Sparkles';

const LandingPage: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-white">
        <Sparkles color="#3B82F6" count={6} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-1/2">
              <h1 className="text-4xl md:text-5xl font-bold text-primary leading-tight">
                Intelligent Feedback for Better Learning
              </h1>
              <p className="mt-4 text-xl text-gray-600">
                AiLearn transforms education with AI-powered, conversational feedback on assignments, making learning more engaging and effective.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/demo" className="btn bg-accent-1 text-gray-800 hover:bg-opacity-90 text-center">
                  Request Demo
                </Link>
                <Link to="/instructor" className="btn bg-primary text-white hover:bg-primary-dark text-center">
                  Try AiLearn
                </Link>
              </div>
            </div>
            <div className="mt-10 md:mt-0 md:w-1/2">
              <div className="relative mx-auto w-full rounded-lg shadow-xl overflow-hidden">
                <div className="aspect-w-16 aspect-h-9">
                  <div className="bg-gray-100 rounded-lg p-6 h-full flex items-center justify-center">
                    <div className="card w-full max-w-md mx-auto">
                      <div className="flex items-start mb-4">
                        <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-bold">AI</div>
                        <div className="ml-4 bg-gray-100 rounded-lg p-3">
                          <p className="text-sm">Your essay shows strong critical thinking, but needs more evidence to support your main argument. Would you like specific suggestions?</p>
                        </div>
                      </div>
                      <div className="flex items-start justify-end">
                        <div className="mr-4 bg-primary bg-opacity-10 rounded-lg p-3">
                          <p className="text-sm">Yes, please show me where I need more evidence.</p>
                        </div>
                        <div className="h-10 w-10 rounded-full bg-accent-2 flex items-center justify-center text-white font-bold">S</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-primary">How AiLearn Works</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              A simple, powerful workflow for both instructors and students
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="card text-center">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto text-xl font-bold">1</div>
              <h3 className="mt-6 text-xl font-medium text-primary">Create & Define</h3>
              <p className="mt-2 text-gray-600">Instructors create assignments and define custom rubrics that align with learning objectives.</p>
            </div>

            <div className="card text-center">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto text-xl font-bold">2</div>
              <h3 className="mt-6 text-xl font-medium text-primary">Submit & Analyze</h3>
              <p className="mt-2 text-gray-600">Students submit work in any language. AI analyzes against rubrics and learning goals.</p>
            </div>

            <div className="card text-center">
              <div className="h-12 w-12 rounded-full bg-primary text-white flex items-center justify-center mx-auto text-xl font-bold">3</div>
              <h3 className="mt-6 text-xl font-medium text-primary">Dialogue & Improve</h3>
              <p className="mt-2 text-gray-600">Students engage in conversation with AI about their work, asking questions and making revisions.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-primary">Features Overview</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful tools to enhance the learning experience
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="card">
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-primary">Rubric-Based Feedback</h3>
              <p className="mt-2 text-sm text-gray-600">Customizable rubrics ensure feedback aligns with specific learning objectives.</p>
            </div>

            <div className="card">
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-primary">Multilingual Support</h3>
              <p className="mt-2 text-sm text-gray-600">Submit and receive feedback in multiple languages, making education more accessible.</p>
            </div>

            <div className="card">
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-primary">Code & Essay Support</h3>
              <p className="mt-2 text-sm text-gray-600">Versatile feedback for various assignment types, from essays to programming projects.</p>
            </div>

            <div className="card">
              <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-primary">Conversational AI</h3>
              <p className="mt-2 text-sm text-gray-600">Interactive dialogue with AI helps students understand feedback and improve their work.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-primary">What Educators & Students Say</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              AiLearn is transforming educational experiences
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="card">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-bold">JD</div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Dr. Jane Davis</h4>
                  <p className="text-sm text-gray-600">Professor of Computer Science</p>
                </div>
              </div>
              <p className="text-gray-600">"AiLearn has revolutionized how I provide feedback to my programming students. They're more engaged with the revision process and the quality of final submissions has improved dramatically."</p>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-bold">MR</div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Michael Rodriguez</h4>
                  <p className="text-sm text-gray-600">Engineering Student</p>
                </div>
              </div>
              <p className="text-gray-600">"Being able to ask questions about feedback and get immediate responses helped me understand exactly what I needed to improve. It's like having a tutor available 24/7."</p>
            </div>

            <div className="card">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-primary bg-opacity-20 flex items-center justify-center text-primary font-bold">SK</div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Sarah Kim</h4>
                  <p className="text-sm text-gray-600">Department Chair, English</p>
                </div>
              </div>
              <p className="text-gray-600">"The multilingual support has been a game-changer for our international students. They can submit work in their native language while still receiving quality feedback aligned with our standards."</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to transform feedback?</span>
            <span className="block text-secondary">Start with AiLearn today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link to="/demo" className="btn bg-accent-1 text-gray-800 hover:bg-opacity-90">
                Request Demo
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link to="/pricing" className="btn bg-white text-primary hover:bg-gray-50">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Markers */}
      <div className="bg-neutral py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <p className="text-center text-sm font-medium text-gray-600">Secure & Private</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <p className="text-center text-sm font-medium text-gray-600">Built with AI</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <p className="text-center text-sm font-medium text-gray-600">Multilingual Support</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <p className="text-center text-sm font-medium text-gray-600">24/7 Feedback</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
